import Seo from "../components/Seo";
import { useI18n } from "../i18n/LanguageProvider";
import { useRegisterAlternates } from "../components/layout/Layout";
import { pagePath } from "../config/pages";
import { LOCATIONS } from "../data/locations";
import { PageHero, CTABand, SectionHeading } from "../components/Sections";
import { Reveal, RevealStagger, RevealItem } from "../components/motion/Reveal";
import { LocationCard } from "../components/Cards";
import Technology from "../components/Technology";
import { webPageSchema, breadcrumbSchema } from "../lib/schema";

const L = (en, fr, de) => ({ en, fr, de });

const TOURISM_SECTIONS = [
  {
    h: L("Why patients consider Vietnam", "Pourquoi les patients envisagent le Vietnam", "Warum Patienten Vietnam in Betracht ziehen"),
    body: L(
      ["Many international patients explore Vietnam because selected providers may combine modern facilities, experienced dentists and competitive treatment costs, with the option to combine care with travel. Standards vary between clinics, so the value comes from choosing the right provider — not the country alone."],
      ["De nombreux patients internationaux explorent le Vietnam car certains prestataires sélectionnés peuvent associer installations modernes, dentistes expérimentés et coûts compétitifs, avec la possibilité de combiner soins et voyage. Les standards varient d’une clinique à l’autre : la valeur vient du bon choix de prestataire, pas du pays seul."],
      ["Viele internationale Patienten erkunden Vietnam, weil ausgewählte Anbieter moderne Einrichtungen, erfahrene Zahnärzte und wettbewerbsfähige Kosten verbinden können — mit der Option, Behandlung und Reise zu kombinieren. Die Standards variieren je nach Klinik: Der Wert liegt in der Wahl des richtigen Anbieters, nicht im Land allein."]
    ),
  },
  {
    h: L("How to research providers", "Comment évaluer les prestataires", "Wie man Anbieter recherchiert"),
    list: L(
      ["Confirm the clinic licence and the dentist’s credentials and experience", "Ask which implant brands, materials and technology (e.g. CBCT) are used", "Request a written treatment plan with clear, itemised costs", "Understand warranty terms and how aftercare works once you return home"],
      ["Confirmez la licence de la clinique et les diplômes et l’expérience du dentiste", "Demandez les marques d’implants, matériaux et technologies utilisés (ex. CBCT)", "Demandez un plan de traitement écrit avec des coûts détaillés et clairs", "Comprenez les conditions de garantie et le suivi une fois rentré chez vous"],
      ["Bestätigen Sie Kliniklizenz sowie Qualifikationen und Erfahrung des Zahnarztes", "Fragen Sie nach Implantatmarken, Materialien und Technik (z. B. CBCT)", "Verlangen Sie einen schriftlichen Behandlungsplan mit klaren, aufgeschlüsselten Kosten", "Verstehen Sie Garantiebedingungen und die Nachsorge nach Ihrer Rückkehr"]
    ),
  },
  {
    h: L("Planning before you travel", "Planifier avant de partir", "Planung vor der Reise"),
    body: L(
      ["Gather recent dental photos and any X-rays or CBCT scans, and share your concerns and goals for an initial, informal review. This helps you understand possible options and indicative estimates before you commit — but it is not a diagnosis. A licensed dentist must examine you in person to confirm your treatment and its suitability."],
      ["Rassemblez des photos dentaires récentes et vos radiographies ou scanners CBCT, et exposez vos préoccupations et objectifs pour une première évaluation informelle. Cela aide à comprendre les options possibles et des estimations indicatives avant de vous engager — mais ce n’est pas un diagnostic. Un dentiste agréé doit vous examiner en personne pour confirmer le traitement et son indication."],
      ["Sammeln Sie aktuelle Zahnfotos sowie Röntgen- oder CBCT-Aufnahmen und schildern Sie Anliegen und Ziele für eine erste, informelle Prüfung. So verstehen Sie mögliche Optionen und Richtwerte, bevor Sie sich festlegen — es ist jedoch keine Diagnose. Ein lizenzierter Zahnarzt muss Sie persönlich untersuchen, um Behandlung und Eignung zu bestätigen."]
    ),
  },
  {
    h: L("Travel, length of stay & multi-stage treatment", "Voyage, durée du séjour et traitement en plusieurs étapes", "Reise, Aufenthaltsdauer & mehrstufige Behandlung"),
    body: L(
      ["Length of stay depends entirely on your treatment. Some treatments finish in a few days, while implants often need a healing period and may require more than one trip. Plan travel, accommodation and follow-up time around the timeline your provider gives you, and confirm arrangements before booking flights."],
      ["La durée du séjour dépend entièrement de votre traitement. Certains soins se terminent en quelques jours, tandis que les implants nécessitent souvent une cicatrisation et parfois plusieurs voyages. Planifiez voyage, hébergement et suivi selon le calendrier de votre prestataire, et confirmez avant de réserver vos vols."],
      ["Die Aufenthaltsdauer hängt ganz von Ihrer Behandlung ab. Manche Behandlungen sind in wenigen Tagen fertig, während Implantate oft eine Einheilphase und mehrere Reisen erfordern. Planen Sie Reise, Unterkunft und Nachsorge nach dem Zeitplan Ihres Anbieters und bestätigen Sie alles vor der Flugbuchung."]
    ),
  },
  {
    h: L("Balanced view: potential risks", "Vue équilibrée : risques potentiels", "Ausgewogene Sicht: mögliche Risiken"),
    body: L(
      ["Travelling for treatment carries real considerations. Follow-up care can be harder to arrange from abroad, complications may require a return visit, and quality varies between clinics. These risks can be reduced with careful research, a clear written plan and a provider who offers structured aftercare — but they should be weighed honestly before you decide."],
      ["Se déplacer pour un traitement comporte de réelles considérations. Le suivi peut être plus difficile à organiser depuis l’étranger, des complications peuvent nécessiter un nouveau déplacement, et la qualité varie selon les cliniques. Ces risques peuvent être réduits par une recherche soignée, un plan écrit clair et un prestataire proposant un suivi structuré — mais ils doivent être pesés honnêtement avant de décider."],
      ["Für eine Behandlung zu reisen bringt echte Überlegungen mit sich. Nachsorge lässt sich aus dem Ausland schwerer organisieren, Komplikationen können eine erneute Reise erfordern, und die Qualität variiert je nach Klinik. Diese Risiken lassen sich durch sorgfältige Recherche, einen klaren schriftlichen Plan und einen Anbieter mit strukturierter Nachsorge verringern — sollten aber vor der Entscheidung ehrlich abgewogen werden."]
    ),
  },
];

const META = {
  title: {
    en: "Dental Tourism in Vietnam | Destinations & Guide for International Patients",
    fr: "Tourisme dentaire au Vietnam | Destinations et guide pour patients internationaux",
    de: "Zahntourismus in Vietnam | Reiseziele & Ratgeber für internationale Patienten",
  },
  desc: {
    en: "Considering dental tourism in Vietnam? Compare the top destinations — Ho Chi Minh City, Da Nang and Hanoi — and understand how to plan your visit.",
    fr: "Vous envisagez le tourisme dentaire au Vietnam ? Comparez les principales destinations — Hô Chi Minh-Ville, Da Nang et Hanoï — et organisez votre séjour.",
    de: "Zahntourismus in Vietnam? Vergleichen Sie die Top-Ziele — Ho-Chi-Minh-Stadt, Da Nang und Hanoi — und planen Sie Ihren Besuch.",
  },
  h1: {
    en: "Dental Tourism in Vietnam",
    fr: "Tourisme dentaire au Vietnam",
    de: "Zahntourismus in Vietnam",
  },
  intro: {
    en: "Vietnam is a popular destination that some international patients explore for dental care, with the option to combine treatment with travel. Below are the destinations patients most often consider.",
    fr: "Le Vietnam est une destination prisée que certains patients internationaux explorent pour les soins dentaires, avec la possibilité d’associer traitement et voyage. Voici les destinations les plus souvent envisagées.",
    de: "Vietnam ist ein beliebtes Ziel, das manche internationalen Patienten für Zahnbehandlungen erkunden — mit der Option, Behandlung und Reise zu verbinden. Nachfolgend die am häufigsten in Betracht gezogenen Ziele.",
  },
};

export default function DentalTourism() {
  const { t, lang, p } = useI18n();
  const alternates = { en: pagePath("dentalTourism", "en"), fr: pagePath("dentalTourism", "fr"), de: pagePath("dentalTourism", "de") };
  useRegisterAlternates(alternates);
  const path = pagePath("dentalTourism", lang);
  const title = META.h1[lang] || META.h1.en;

  return (
    <>
      <Seo
        title={META.title[lang] || META.title.en}
        description={META.desc[lang] || META.desc.en}
        path={path}
        alternates={alternates}
        lang={lang}
        jsonLd={[
          webPageSchema({ name: title, description: META.desc[lang] || META.desc.en, path }),
          breadcrumbSchema([{ name: t.common.home, path: p("home") }, { name: title, path }]),
        ]}
      />
      <PageHero eyebrow={t.locationsSection.eyebrow} title={title} intro={META.intro[lang] || META.intro.en} crumbs={[{ name: title, to: path }]} />

      <section className="container-dv py-16 lg:py-20">
        <RevealStagger className="grid gap-6 md:grid-cols-3">
          {LOCATIONS.map((lo) => (
            <RevealItem key={lo.key}>
              <LocationCard location={lo} />
            </RevealItem>
          ))}
        </RevealStagger>
      </section>

      <section className="container-dv pb-4 lg:pb-8">
        <div className="max-w-3xl space-y-8">
          {TOURISM_SECTIONS.map((s, i) => (
            <Reveal key={i} delay={0.03 * i}>
              <section>
                <h2 className="text-2xl sm:text-3xl text-navy">{s.h[lang] || s.h.en}</h2>
                {s.body && (
                  <div className="prose-dv mt-3">
                    {(s.body[lang] || s.body.en).map((para, j) => <p key={j}>{para}</p>)}
                  </div>
                )}
                {s.list && (
                  <ul className="mt-3 space-y-2.5">
                    {(s.list[lang] || s.list.en).map((li, j) => (
                      <li key={j} className="flex gap-3 text-navy/75 text-base sm:text-lg leading-relaxed">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-turquoise" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </Reveal>
          ))}
          <div className="rounded-2xl border border-navy/10 bg-mist p-6 text-sm leading-relaxed text-navy/70">
            <strong className="text-navy">{t.common.disclaimerTitle}.</strong> {t.common.disclaimer}
          </div>
        </div>
      </section>

      <section className="bg-mist py-16 lg:py-20">
        <div className="container-dv">
          <Reveal>
            <SectionHeading eyebrow={t.howItWorks.eyebrow} title={t.howItWorks.title} sub={t.howItWorks.sub} />
          </Reveal>
          <RevealStagger className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {t.howItWorks.steps.map((s, i) => (
              <RevealItem key={i}>
                <div className="h-full rounded-3xl border border-navy/8 bg-white p-6 shadow-soft">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-dental text-white font-bold">{i + 1}</span>
                  <h3 className="mt-4 text-lg text-navy">{s.t}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-navy/65">{s.d}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      <Technology showImplantSystems={false} />

      <CTABand />
    </>
  );
}
